'use client'

import { AlertTriangle, LogOut, Mail } from 'lucide-react'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/stores/auth-store'
import { useThemeStore } from '@/lib/stores/theme-store'
import { trackEvent } from '@/lib/analytics'

type BlockedReason = 'member-blocked' | 'plan-expired'

const BLOCK_COPY: Record<BlockedReason, { title: string; lead: string }> = {
  'member-blocked': {
    title: 'Área de membros indisponível',
    lead: 'Esta área de membros está temporariamente indisponível.',
  },
  'plan-expired': {
    title: 'Esta área não está mais ativa',
    lead: 'Esta área de membros não está mais ativa no momento.',
  },
}

export function BlockedMemberArea({ reason = 'member-blocked' }: { reason?: BlockedReason }) {
  const router = useRouter()
  const { currentTheme } = useThemeStore()
  const { isAuthenticated, logout } = useAuthStore()
  const mentorEmail = currentTheme?.mentor_email
  const copy = BLOCK_COPY[reason]

  useEffect(() => {
    trackEvent('locked_content_viewed', { kind: reason })
  }, [reason])

  const handleLogout = async () => {
    try {
      await logout()
    } finally {
      router.push('/?force_login=1')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4" style={{ backgroundColor: 'var(--body-bg, #0a0a0a)' }}>
      <div className="w-full max-w-md rounded-lg border border-orange-500/30 bg-neutral-950/95 p-6 text-white shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-orange-500/10 text-orange-400">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <h1 className="text-xl font-semibold">{copy.title}</h1>
        <p className="mt-3 text-sm leading-6 text-neutral-300">
          {copy.lead}
          {mentorEmail ? (
            <>
              {' '}Busque mais informações pelo email{' '}
              <a className="font-medium text-orange-300 underline underline-offset-2" href={`mailto:${mentorEmail}`}>
                {mentorEmail}
              </a>
              .
            </>
          ) : (
            ' Busque mais informações com o mentor responsável.'
          )}
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          {mentorEmail && (
            <Button asChild className="bg-orange-500 text-white hover:bg-orange-600">
              <a href={`mailto:${mentorEmail}`}>
                <Mail className="mr-2 h-4 w-4" />
                Enviar email
              </a>
            </Button>
          )}
          {/* No motivo plan-expired o logout é automático (MainLayout); o botão
              manual sairia em corrida com ele, então só aparece no member-blocked. */}
          {reason === 'member-blocked' && isAuthenticated && (
            <Button type="button" variant="secondary" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
